import React, { useState, useCallback } from 'react';
import Modal from './Modal';
import { todayInGaborone } from '../../utils/dates';

const DEFAULT_CATEGORY_OPTIONS = [
  'Research', 'Printing', 'Travel', 'Lodging', 'Filing Fees', 'Consultant', 'Meals', 'Accommodation', 'Serving', 'Other',
].map(v => ({ value: v, label: v }));

const blankForm = () => ({
  date: todayInGaborone(), amount: '', category: 'Filing Fees', description: '', receipt: null, billable: true,
});

/**
 * Standalone "Log Disbursement" (expense) form, extracted from
 * TimeExpenseManagement.js's "Add Disbursement" form so it can be
 * embedded inline (locked to one case/client) instead of only reachable
 * from the global Time & Expense Management page. Mirrors that form's
 * behavior of refreshing invoices after creation.
 */
export default function LogDisbursementModal({
  open,
  onClose,
  onCreated,
  authAxios,
  showFormMessage,
  caseId,
  clientId,
  fetchInvoices,
  expenseCategoryOptions = DEFAULT_CATEGORY_OPTIONS,
}) {
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const fld = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') { setForm(f => ({ ...f, receipt: files?.[0] || null })); return; }
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      const payload = { ...form, case: caseId || '', client: clientId || '' };
      Object.keys(payload).forEach(key => {
        if (payload[key] !== null && payload[key] !== '') formData.append(key, payload[key]);
      });
      await authAxios.post('expense-entries/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showFormMessage?.('success', 'Disbursement added successfully!');
      setForm(blankForm());
      onCreated?.();
      await fetchInvoices?.();
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data ? JSON.stringify(err.response.data) : (err?.message || 'Failed to add disbursement.');
      showFormMessage?.('danger', `Failed to add disbursement. ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [authAxios, form, caseId, clientId, showFormMessage, onCreated, onClose, fetchInvoices]);

  return (
    <Modal open={open} onClose={onClose} title="Log Disbursement" maxWidth="max-w-lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Date</label>
            <input type="date" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="date" value={form.date} onChange={fld} required autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {form.category === 'Serving' ? 'Rate (per km/unit)' : 'Amount'}
            </label>
            <input type="number" step="0.01" className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
              name="amount" value={form.amount} onChange={fld} required />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Category</label>
          <select className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm"
            name="category" value={form.category} onChange={fld}>
            {expenseCategoryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm" rows={3}
            name="description" value={form.description} onChange={fld} />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Receipt (optional)</label>
          <input type="file" className="mt-1 block w-full text-sm" name="receipt" onChange={fld} />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            name="billable" checked={form.billable} onChange={fld} />
          Recoverable from client
        </label>
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
            {saving ? 'Logging…' : 'Log Disbursement'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
